import { D } from '@mobily/ts-belt';
import { Box } from '@mui/material';
import { useMemo } from 'react';
import {
  FunctionField,
  NumberField,
  RaRecord,
  ReferenceManyField,
  TextField,
  TopToolbar,
  useRecordContext,
  useTranslate,
  WithRecord,
} from 'react-admin';

import { generatePlanTypeLabeled } from '@Common/bo/b2b_campaigns';
import {
  GetB2bCampaignAllocationOneResponse,
  GetB2bCampaignAllocationsQuery,
  GetB2bCampaignOneResponse,
  getB2bCampaignAllocationsSortColumns,
} from '@Common/types/bo/b2b/campaigns';

import { DatagridTyped, Pagination, ShowSection } from '@sh4res/bo-common/components';
import { CampaignAllocationsExportButtons } from '@sh4res/bo-common/components/B2bCampaigns/CampaignAllocationsExportButtons';
import { taxResidenceCodeChoices } from '@sh4res/bo-common/components/B2bEmployee/choices';
import { SelectColumnsButton } from '@sh4res/bo-common/components/SelectColumnsButton';
import { CurrencyField, IdField, SelectChipField } from '@sh4res/bo-common/fields';
import { ResourceBasePath, useResourceBasePath, useSelectedColumns } from '@sh4res/bo-common/hooks';
import { ColumnList, RaSort, Resources } from '@sh4res/bo-common/utils';

const testId = 'BoExternalB2bCampaignsShow';

type B2bCampaignSimplifiedAllocation = Pick<
  GetB2bCampaignAllocationOneResponse,
  | 'id'
  | 'employee_id'
  | 'firstname'
  | 'lastname'
  | 'identifier'
  | 'social_status'
  | 'net_amount'
  | 'matching_contribution_amount'
  | 'cet_days'
  | 'rtt_days'
  | 'overtime_days'
  | 'paid_leave_days'
  | 'seniority_leave_days'
  | 'untaken_rest_days'
  | 'invested_cet_days'
  | 'invested_rtt_days'
  | 'invested_overtime_days'
  | 'invested_paid_leave_days'
  | 'invested_seniority_leave_days'
  | 'invested_untaken_rest_days'
>;

// Days columns only displayed for vacation days (CET) campaigns
const vacationDaysColumns = [
  'cet_days',
  'invested_cet_days',
  'rtt_days',
  'invested_rtt_days',
  'overtime_days',
  'invested_overtime_days',
  'paid_leave_days',
  'invested_paid_leave_days',
  'seniority_leave_days',
  'invested_seniority_leave_days',
  'untaken_rest_days',
  'invested_untaken_rest_days',
] as const satisfies (keyof B2bCampaignSimplifiedAllocation)[];

const CampaignAllocationsList = <T extends RaRecord>({
  record,
  resourceBasePath,
  columnRegistry,
  defaultColumns,
  sortableColumns,
}: {
  record: GetB2bCampaignOneResponse;
  resourceBasePath: ResourceBasePath;
  columnRegistry: ColumnList<T>;
  defaultColumns: (keyof T)[];
  sortableColumns: (keyof T)[] | readonly (keyof T)[];
}) => {
  const displayedColumns = useSelectedColumns<T>({
    preferences: 'b2b_campaigns_allocations.list.columns',
    defaultColumns,
  });

  return (
    <ReferenceManyField
      reference={`${resourceBasePath}campaigns/allocations`}
      target="b2b_campaign_id"
      filter={{ campaign_id: record.id }}
      sort={allocationsDefaultSort}
      perPage={10}
      data-testid={`${testId}_AllocationsField`}>
      <TopToolbar>
        <SelectColumnsButton<T>
          preference="b2b_campaigns_allocations.list.columns"
          columns={columnRegistry}
          defaultColumns={defaultColumns}
          testId={`${testId}_CampaignAllocationSelectColumnsButton`}
        />
        <CampaignAllocationsExportButtons record={record} resourceBasePath={resourceBasePath} />
      </TopToolbar>

      <DatagridTyped<T>
        sortableColumns={sortableColumns}
        columnRegistry={columnRegistry}
        displayedColumns={displayedColumns}
        bulkActionButtons={false}
        showCard={false}
        sx={{
          '& .MuiTableCell-head': { minWidth: '140px', verticalAlign: 'middle' },
        }}
      />

      <Pagination />
    </ReferenceManyField>
  );
};

const campaignAllocationsListColumns: (
  resourceBasePath: ResourceBasePath,
) => ColumnList<GetB2bCampaignAllocationOneResponse> = (resourceBasePath) => ({
  id: <NumberField data-testid={`${testId}_AllocationIdField`} />,
  company_id: (
    <IdField
      reference={`${resourceBasePath}companies`}
      data-testid={`${testId}_AllocationCompanyIdField`}
    />
  ),
  campaign_id: (
    <IdField
      reference={`${resourceBasePath}campaigns`}
      data-testid={`${testId}_AllocationCampaignIdField`}
    />
  ),
  employee_id: (
    <IdField
      reference={`${resourceBasePath}employees`}
      data-testid={`${testId}_AllocationEmployeeIdField`}
    />
  ),
  firstname: <TextField source="firstname" data-testid={`${testId}_AllocationFirstnameField`} />,
  lastname: <TextField source="lastname" data-testid={`${testId}_AllocationLastnameField`} />,
  identifier: <TextField source="identifier" data-testid={`${testId}_AllocationIdentifierField`} />,
  registration_number: (
    <TextField
      source="registration_number"
      data-testid={`${testId}_AllocationRegistrationNumberField`}
    />
  ),
  social_status: (
    <TextField source="social_status" data-testid={`${testId}_AllocationSocialStatusField`} />
  ),
  tax_residence_code: (
    <SelectChipField
      source="tax_residence_code"
      choices={taxResidenceCodeChoices}
      data-testid={`${testId}_AllocationTaxResidenceCodeField`}
    />
  ),
  gross_amount: <CurrencyField currency="EUR" testId={`${testId}_AllocationGrossAmountField`} />,
  net_amount: <CurrencyField currency="EUR" testId={`${testId}_AllocationNetAmountField`} />,
  entry_fee_amount: (
    <CurrencyField currency="EUR" testId={`${testId}_AllocationEntryFeeAmountField`} />
  ),
  net_invested_amount: (
    <CurrencyField currency="EUR" testId={`${testId}_AllocationNetInvestedAmountField`} />
  ),
  invested_amount: (
    <CurrencyField currency="EUR" testId={`${testId}_AllocationInvestedAmountField`} />
  ),
  matching_contribution_amount: (
    <CurrencyField currency="EUR" testId={`${testId}_AllocationMatchingContributionAmountField`} />
  ),
  payout_amount: <CurrencyField currency="EUR" testId={`${testId}_AllocationPayoutAmountField`} />,
  late_interests_amount: (
    <CurrencyField currency="EUR" testId={`${testId}_AllocationLateInterestsAmountField`} />
  ),
  csg_amount: (
    <CurrencyField
      currency="EUR"
      source="csg_amount"
      testId={`${testId}_AllocationCsgAmountField`}
    />
  ),
  crds_amount: (
    <CurrencyField
      currency="EUR"
      source="crds_amount"
      testId={`${testId}_AllocationCrdsAmountField`}
    />
  ),
  company_collected_choices: (
    <FunctionField<GetB2bCampaignAllocationOneResponse>
      source="company_collected_choices"
      data-testid={`${testId}_AllocationCompanyCollectedChoicesField`}
      render={(record) =>
        record.company_collected_choices
          .map((choice) =>
            choice.plan_type
              ? generatePlanTypeLabeled({
                  planType: choice.plan_type,
                  planLabel: choice.plan_label,
                })
              : choice.allocation_type,
          )
          .filter(Boolean)
          .join(', ')
      }
    />
  ),
  amount_in_days: <NumberField data-testid={`${testId}_AllocationAmountInDaysField`} />,
  cet_days: <NumberField data-testid={`${testId}_AllocationCetDaysField`} />,
  rtt_days: <NumberField data-testid={`${testId}_AllocationRttDaysField`} />,
  overtime_days: <NumberField data-testid={`${testId}_AllocationOvertimeDaysField`} />,
  paid_leave_days: <NumberField data-testid={`${testId}_AllocationPaidLeaveDaysField`} />,
  seniority_leave_days: <NumberField data-testid={`${testId}_AllocationSeniorityLeaveDaysField`} />,
  untaken_rest_days: <NumberField data-testid={`${testId}_AllocationUntakenRestDaysField`} />,
  cet_amount: <CurrencyField currency="EUR" testId={`${testId}_AllocationCetAmountField`} />,
  rtt_amount: <CurrencyField currency="EUR" testId={`${testId}_AllocationRttAmountField`} />,
  overtime_amount: (
    <CurrencyField currency="EUR" testId={`${testId}_AllocationOvertimeAmountField`} />
  ),
  paid_leave_amount: (
    <CurrencyField currency="EUR" testId={`${testId}_AllocationPaidLeaveAmountField`} />
  ),
  seniority_leave_amount: (
    <CurrencyField currency="EUR" testId={`${testId}_AllocationSeniorityLeaveAmountField`} />
  ),
  untaken_rest_amount: (
    <CurrencyField currency="EUR" testId={`${testId}_AllocationUntakenRestAmountField`} />
  ),
  invested_amount_in_days: (
    <NumberField data-testid={`${testId}_AllocationInvestedAmountInDaysField`} />
  ),
  invested_cet_days: <NumberField data-testid={`${testId}_AllocationInvestedCetDaysField`} />,
  invested_rtt_days: <NumberField data-testid={`${testId}_AllocationInvestedRttDaysField`} />,
  invested_overtime_days: (
    <NumberField data-testid={`${testId}_AllocationInvestedOvertimeDaysField`} />
  ),
  invested_paid_leave_days: (
    <NumberField data-testid={`${testId}_AllocationInvestedPaidLeaveDaysField`} />
  ),
  invested_seniority_leave_days: (
    <NumberField data-testid={`${testId}_AllocationInvestedSeniorityLeaveDaysField`} />
  ),
  invested_untaken_rest_days: (
    <NumberField data-testid={`${testId}_AllocationInvestedUntakenRestDaysField`} />
  ),
});

const otherAllocationDefaultColumns: (keyof GetB2bCampaignAllocationOneResponse)[] = [
  'employee_id',
  'firstname',
  'lastname',
  'identifier',
  'social_status',
  'gross_amount',
  'net_amount',
  'entry_fee_amount',
  'net_invested_amount',
  'matching_contribution_amount',
  'payout_amount',
  'late_interests_amount',
];

const simplifiedAllocationDefaultColumns: (keyof B2bCampaignSimplifiedAllocation)[] = [
  'employee_id',
  'firstname',
  'lastname',
  'identifier',
  'social_status',
  'net_amount',
  'matching_contribution_amount',
  ...vacationDaysColumns,
];

const campaignSimplifiedAllocationsListColumns: (
  resourceBasePath: ResourceBasePath,
  netAmountDepositTranslation: string,
) => ColumnList<B2bCampaignSimplifiedAllocation> = (
  resourceBasePath,
  netAmountDepositTranslation,
) => ({
  ...D.selectKeys(
    campaignAllocationsListColumns(resourceBasePath),
    simplifiedAllocationDefaultColumns,
  ),
  net_amount: (
    <CurrencyField
      currency="EUR"
      label={netAmountDepositTranslation}
      testId={`${testId}_AllocationNetAmountField`}
    />
  ),
});

const allocationsDefaultSort: RaSort<GetB2bCampaignAllocationsQuery> = {
  field: 'employee_id',
  order: 'ASC',
};

export const CampaignAllocationSection = () => {
  const resourceBasePath = useResourceBasePath();
  const resource: Resources = `${resourceBasePath}campaigns`;
  const translate = useTranslate();
  const record = useRecordContext<GetB2bCampaignOneResponse>();

  const campaignAllocationsRegistry = useMemo(
    () => campaignAllocationsListColumns(resourceBasePath),
    [resourceBasePath],
  );

  const campaignSimplifiedAllocationsRegistry = useMemo(
    () =>
      campaignSimplifiedAllocationsListColumns(
        resourceBasePath,
        translate(
          record?.type === 'unused_vacation_days'
            ? `resources.${resource}/allocations.fields.net_amount`
            : `resources.${resource}/allocations.fields.net_amount_deposit`,
        ),
      ),
    [resourceBasePath, resource, translate, record],
  );

  if (!record) {
    return null;
  }

  const isSimplifiedView =
    record.type === 'voluntary_deposit' ||
    record.type === 'matching_contribution' ||
    record.type === 'unused_vacation_days';

  return (
    <ShowSection label="resources.bo_common.b2b.campaigns.allocations.label" sx={{ minWidth: 0 }}>
      <Box sx={{ paddingLeft: '15px', paddingRight: '15px' }}>
        <WithRecord
          render={(record: GetB2bCampaignOneResponse) => {
            const isVacationDays = record.type === 'unused_vacation_days';
            const filteredSimplifiedAllocationDefaultColumns =
              simplifiedAllocationDefaultColumns.filter(
                (column) =>
                  (column !== 'net_amount' ||
                    record.type === 'voluntary_deposit' ||
                    isVacationDays) &&
                  (!(vacationDaysColumns as readonly string[]).includes(column) || isVacationDays),
              );

            return isSimplifiedView ? (
              <CampaignAllocationsList<B2bCampaignSimplifiedAllocation>
                record={record}
                resourceBasePath={resourceBasePath}
                columnRegistry={campaignSimplifiedAllocationsRegistry}
                defaultColumns={filteredSimplifiedAllocationDefaultColumns}
                sortableColumns={filteredSimplifiedAllocationDefaultColumns.filter(
                  (column) => !(vacationDaysColumns as readonly string[]).includes(column),
                )}
              />
            ) : (
              <CampaignAllocationsList<GetB2bCampaignAllocationOneResponse>
                record={record}
                resourceBasePath={resourceBasePath}
                columnRegistry={campaignAllocationsRegistry}
                defaultColumns={otherAllocationDefaultColumns}
                sortableColumns={getB2bCampaignAllocationsSortColumns}
              />
            );
          }}
        />
      </Box>
    </ShowSection>
  );
};
